/**
 * Owner-token store (POLISH_04 #1) — a small localStorage-backed `id -> ownerToken`
 * map. The server hands back a secret `ownerToken` ONLY on a brand-new publish (see
 * `PublishResponse.ownerToken`) and never again; this module is where the client keeps
 * it so a later "unpublish my level" action can present it back as `X-Owner-Token`.
 *
 * Best-effort like everything else in src/sharing: localStorage may be absent (SSR/
 * node tests) or full/blocked (private browsing) — every method degrades to a no-op
 * rather than throwing, mirroring MockLevelService's persistence pattern.
 */
const STORE_KEY = 'maker.ownerTokens.v1';
/** Cap the number of remembered tokens so the map can't grow unbounded across a long
 *  play session that publishes many levels — oldest entries drop first (Map preserves
 *  insertion order, so `keys().next()` is the oldest). */
const MAX_ENTRIES = 200;

type TokenMap = Record<string, string>;

function hasStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

function readAll(): TokenMap {
  if (!hasStorage()) return {};
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as TokenMap) : {};
  } catch { return {}; }
}

function writeAll(map: TokenMap): void {
  if (!hasStorage()) return;
  try { localStorage.setItem(STORE_KEY, JSON.stringify(map)); }
  catch { /* quota / private mode — non-fatal, matches MockLevelService.persist() */ }
}

/** Remember `token` for published level `id`. Evicts the oldest entry first if the map
 *  is already at MAX_ENTRIES (bounds unbounded growth, never throws). */
export function rememberOwnerToken(id: string, token: string): void {
  if (!id || !token) return;
  const map = readAll();
  if (!(id in map) && Object.keys(map).length >= MAX_ENTRIES) {
    const oldest = Object.keys(map)[0];
    if (oldest !== undefined) delete map[oldest];
  }
  map[id] = token;
  writeAll(map);
}

/** The remembered owner token for `id`, or undefined if never published from this
 *  browser (or storage was unavailable / cleared). */
export function getOwnerToken(id: string): string | undefined {
  return readAll()[id];
}

/** Every published-level pub_ id this browser holds a capability token for — the "levels
 *  you published from here" set. Used by the menu's Your Levels view to offer Unpublish
 *  without any separate library↔pubId linkage (none exists today: publish() is
 *  fire-and-forget from the editor). Order is insertion order (oldest first); callers
 *  that want newest-first should reverse. Empty array when storage is unavailable/empty. */
export function listOwnerTokenIds(): string[] {
  return Object.keys(readAll());
}

/** Forget a level's token (called after a successful unpublish — the server-side
 *  record is gone, so holding onto the capability is pointless). */
export function forgetOwnerToken(id: string): void {
  if (!hasStorage()) return;
  const map = readAll();
  if (!(id in map)) return;
  delete map[id];
  writeAll(map);
}
